import {useCallback, useEffect} from "react";
import {useSelector} from "react-redux";
import {ToastContent} from "components";
import {toast} from "react-toastify";
import {getImportLinkedinCandidateStatus} from "store/candidate/linkedInProfileImportAsyncActions";
import {
	TCandidateImportRequestResponse,
	TLinkedInCandidateImport,
	TLinkedinUrlImportStatus,
} from "api/candidateApi/types";
import {useAppDispatch, useAppSelector} from "hooks/index";
import {
	currentRefreshLinkedInProfilesScrapeInfoSelector,
	refreshLinkedInCandidatesInProgressSelector,
} from "store/refresh-candidate/refreshCandidateSelectors";
import {clearRefreshLinkedInProfileScrapeInfo} from "store/refresh-candidate/refreshCandidateActions";
import {isEmptyObject} from "helpers/filterHandlers";
import {removeTrailingSlash} from "helpers/string";
import {
	fetchTargetListCandidateIDs,
	getTargetListCandidates,
} from "store/target-list/targetListCandidatesAsyncActions";

/* eslint-disable  @typescript-eslint/no-explicit-any */
const intervalId = {} as any;

const useRefreshLinkedInProfileProgressWatcher = () => {
	const currentRefreshLinkedInProfileScrapeInfo: {[x: string]: TCandidateImportRequestResponse} =
		useSelector(currentRefreshLinkedInProfilesScrapeInfoSelector);

	const currentlyImportingList = useAppSelector(refreshLinkedInCandidatesInProgressSelector);

	const dispatch = useAppDispatch();

	const refreshWatcher = useCallback(
		async (response: TLinkedInCandidateImport, linkedInUrl: string) => {
			const candidate = response?.candidate;

			if (response?.status === "Imported" && candidate) {
				dispatch(clearRefreshLinkedInProfileScrapeInfo(removeTrailingSlash(linkedInUrl)));

				toast.success(ToastContent, {
					autoClose: false,
					closeOnClick: false, // because close option available on the notification
					data: {
						title: `Refreshed ${candidate.full_name}'s profile`,
						description: () => <>We&apos;ll also manually check their profile.</>,
					},
				});

				dispatch(getTargetListCandidates());
				dispatch(fetchTargetListCandidateIDs());
			} else if (response?.error) {
				toast.info(ToastContent, {
					autoClose: false,
					closeOnClick: true,
					data: {
						title:
							"CandidateDetails Import: " + response.message ||
							"Something went wrong!",
					},
				});

				dispatch(clearRefreshLinkedInProfileScrapeInfo(removeTrailingSlash(linkedInUrl)));
			}
		},
		[dispatch],
	);

	const isRefreshInProgress = (status: TLinkedinUrlImportStatus) => {
		return status === "Created" || status === "In Progress" || status === "Scraped";
	};

	/**
	 * Here in the state we have kept the information while user clicked on refresh icon on candidate profile
	 * We check the active refresh state for the search.
	 * Then we watch the refresh status
	 * if refresh is successful, then we redirect the user to the candidate page
	 * if import is failed, then we show a notification with a reason
	 */

	useEffect(() => {
		if (
			currentRefreshLinkedInProfileScrapeInfo &&
			!isEmptyObject(currentRefreshLinkedInProfileScrapeInfo)
		) {
			for (const [linkedInUrl, data] of Object.entries(
				currentRefreshLinkedInProfileScrapeInfo,
			)) {
				const scrapeInfo: TCandidateImportRequestResponse = data;
				const isImporting: {[x: string]: boolean} = currentlyImportingList[linkedInUrl];
				const inProgress = isRefreshInProgress(scrapeInfo?.status);

				if (isImporting && inProgress) {
					intervalId[linkedInUrl] = setInterval(async () => {
						const response = await dispatch(
							getImportLinkedinCandidateStatus(scrapeInfo.id),
						);

						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						await refreshWatcher(response, linkedInUrl);

						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore
						if (response?.status === "Imported" && response?.candidate !== null) {
							clearInterval(intervalId[linkedInUrl]);
						}
					}, 10 * 1000);

					setTimeout(() => {
						clearInterval(intervalId[linkedInUrl]);
					}, 60 * 30 * 1000); // it will clear after 30 min
				}
			}
		}

		return () => {
			for (const [key] of Object.entries(intervalId)) {
				clearInterval(intervalId[key]);
			}
		};
	}, [currentRefreshLinkedInProfileScrapeInfo, currentlyImportingList, dispatch, refreshWatcher]);
};

export default useRefreshLinkedInProfileProgressWatcher;
